/**
 * Widget → review-scenario mapping for the one-click "Review X" AI buttons.
 *
 * The prompts, labels, and slash commands live server-side as scenarios (COMS
 * scenarios/reviewScenarios.ts) and arrive via `GET /ai/catalog` (see
 * useAICatalog) — one source of truth shared by the buttons, user discovery,
 * manual QA, and future automated evals. This file keeps only the stable
 * widget-facing keys (so call sites don't churn when scenarios are renamed)
 * plus a fallback label rendered while the catalog is loading or unavailable.
 *
 * One entry per ICS widget with a server-side review scenario — 13 today.
 * Adding a capability = one scenario server-side + one key here + one
 * <AIReviewButton> on the owning widget (see DEFINITION_OF_DONE.md in COMS).
 */

export interface AIReviewScenarioRef {
  /** Server scenario name (Scenario.name in the catalog response). */
  scenarioName: string;
  /**
   * Button text rendered before the catalog loads (kept in sync with the
   * scenario's ui.buttonLabel).
   */
  fallbackLabel: string;
}

export const AI_REVIEW_SCENARIOS = {
  objectives: {
    scenarioName: 'review_objectives',
    fallbackLabel: 'Review Objectives',
  },
  actions: { scenarioName: 'review_actions', fallbackLabel: 'Review Actions' },
  safetyMessages: {
    scenarioName: 'review_safety_messages',
    fallbackLabel: 'Review Safety Messages',
  },
  hazmat: { scenarioName: 'review_hazmat', fallbackLabel: 'Review HAZMAT' },
  medicalPlan: {
    scenarioName: 'review_medical_plan',
    fallbackLabel: 'Review Medical Plan',
  },
  communicationsPlan: {
    scenarioName: 'review_communications_plan',
    fallbackLabel: 'Review Communications Plan',
  },
  assignments: {
    scenarioName: 'review_assignments',
    fallbackLabel: 'Review Assignments',
  },
  safetyAnalysis215A: {
    scenarioName: 'review_iap_safety_analysis',
    fallbackLabel: 'Review IAP Safety Analysis',
  },
  workAnalysis: {
    scenarioName: 'review_work_analysis',
    fallbackLabel: 'Review Work Analysis',
  },
  workAssignments: {
    scenarioName: 'review_work_assignments',
    fallbackLabel: 'Review Work Assignments',
  },
  safetyAnalysis: {
    scenarioName: 'review_safety_analysis',
    fallbackLabel: 'Review Safety Analysis',
  },
  resources: {
    scenarioName: 'review_resources',
    fallbackLabel: 'Review Resources',
  },
  roster: { scenarioName: 'review_roster', fallbackLabel: 'Review Roster' },
} as const satisfies Record<string, AIReviewScenarioRef>;

export type AIReviewActionKey = keyof typeof AI_REVIEW_SCENARIOS;

/**
 * The text prefilled (never auto-sent) into the chat input when a scenario
 * launches. Ui-bearing scenarios keep their visible "<slash command> " prefix
 * so users get comfortable with the slash syntax; other scenarios prefill the
 * prompt as-is.
 */
export const buildScenarioPrefill = (scenario: {
  prompt: string;
  ui?: { command: string };
}): string =>
  scenario.ui ? `${scenario.ui.command} ${scenario.prompt}` : scenario.prompt;
